import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { I18nStore, LayoutStore, PlatformStore } from '@indutrust/shared-data';
import { APP_SESSION, type SideMenuCategory } from '@indutrust/shared-model';
import {
  isSideMenuItemRouteActive,
  normalizeSideMenuRoutePath,
  provideTranslocoScopeSharedGlobal,
  provideTranslocoScopeSharedSideMenuContent,
} from '@indutrust/shared-util';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Divider } from 'primeng/divider';
import { Tag } from 'primeng/tag';
import { filter, map } from 'rxjs';
import { I18nComponent } from '../i18n/i18n.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import {
  filterHelpConfigsForSideMenu,
  sideMenuHasRoute,
} from '../topbar/resolve-help-menu-items.util';
import { TOPBAR_HELP_ITEMS_FACTORY } from '../topbar/topbar-help-items.token';

/**
 * Side menu container rendering navigation items grouped by category.
 */
@Component({
  selector: 'idt-side-menu-content',
  imports: [
    Divider,
    I18nComponent,
    RouterLink,
    RouterLinkActive,
    Tag,
    ThemeToggleComponent,
    TranslocoDirective,
  ],
  standalone: true,
  template: `
    <ul *transloco="let tApp; prefix: 'appSm'" class="layout-menu">
      @for (category of sideMenuCategories(); track category.id) {
        <li
          class="layout-root-menuitem"
          [class.active-menuitem]="
            category.collapsible && expandedCategoryState()[category.id]
          "
          [class.layout-root-menuitem--collapsible]="category.collapsible">
          @if (category.collapsible) {
            <a
              tabindex="0"
              [attr.aria-controls]="category.id + '-items'"
              [attr.aria-expanded]="expandedCategoryState()[category.id]"
              [id]="category.id + '-toggle'"
              (click)="toggleCategory(category.id)"
              (keydown.enter)="toggleCategory(category.id)"
              (keydown.space)="onCategoryToggleKeydown($event, category.id)">
              @if (category.icon) {
                <i [class]="'layout-menuitem-icon pi pi-' + category.icon"></i>
              }
              <span>{{ tApp(category.label) }}</span>
              <i class="pi pi-chevron-down layout-submenu-toggler"></i>
            </a>
          } @else {
            <div class="layout-menuitem-root-text">
              @if (category.icon) {
                <i [class]="'pi pi-' + category.icon"></i>
              }
              {{ tApp(category.label) }}
            </div>
          }

          @if (expandedCategoryState()[category.id]) {
            <ul [id]="category.id + '-items'">
              @for (item of category.items; track item.id) {
                <li [class.opacity-50]="item.disabled">
                  <a
                    [class.active-route]="activeMenuItemIds()[item.id]"
                    [class.pointer-events-none]="item.disabled"
                    [routerLink]="item.route">
                    <i [class]="'layout-menuitem-icon pi pi-' + item.icon"></i>
                    <span>{{ tApp(item.title) }}</span>
                    @if (item.tag) {
                      <p-tag
                        class="menu-item-tag"
                        severity="info"
                        [rounded]="true"
                        [value]="tApp(item.tag)" />
                    }
                  </a>
                </li>
              }
            </ul>
          }
          @if (!$last) {
            <p-divider />
          }
        </li>
      }
    </ul>

    <div class="mt-auto">
      <p-divider />
    </div>

    @if (platformStore.isSmallScreen()) {
      <ul class="layout-menu">
        @if (showMobileHelpSection()) {
          <li
            class="layout-root-menuitem layout-root-menuitem--collapsible"
            data-testid="sidebar-mobile-help"
            [class.active-menuitem]="mobileHelpExpanded()">
            <a
              id="mobile-help-toggle"
              tabindex="0"
              [attr.aria-controls]="'mobile-help-items'"
              [attr.aria-expanded]="mobileHelpExpanded()"
              (click)="mobileHelpExpanded.set(!mobileHelpExpanded())"
              (keydown.enter)="mobileHelpExpanded.set(!mobileHelpExpanded())"
              (keydown.space)="
                $event.preventDefault();
                mobileHelpExpanded.set(!mobileHelpExpanded())
              ">
              <span>{{ mobileHelpTitle() }}</span>
              <i class="pi pi-chevron-down layout-submenu-toggler"></i>
            </a>
            @if (mobileHelpExpanded()) {
              <ul id="mobile-help-items">
                @for (item of mobileHelpLabeledItems(); track item.labelKey) {
                  <li>
                    @if (item.routerLink) {
                      <a
                        routerLinkActive="active-route"
                        [routerLink]="item.routerLink">
                        <i [class]="item.icon + ' layout-menuitem-icon'"></i>
                        <span>{{ item.label }}</span>
                      </a>
                    } @else {
                      <a
                        [attr.rel]="
                          item.target === '_blank'
                            ? 'noopener noreferrer'
                            : null
                        "
                        [href]="item.url"
                        [target]="item.target"
                        (click)="closeMobileSidebarIfOpen()">
                        <i [class]="item.icon + ' layout-menuitem-icon'"></i>
                        <span>{{ item.label }}</span>
                      </a>
                    }
                  </li>
                }
                @if (showMobileSearchLink()) {
                  <li>
                    <a
                      data-testid="sidebar-mobile-search"
                      routerLink="/search"
                      routerLinkActive="active-route">
                      <i class="layout-menuitem-icon pi pi-search"></i>
                      <span>{{ mobileSearchTitle() }}</span>
                    </a>
                  </li>
                }
              </ul>
            }
          </li>
        }
        <p-divider />
      </ul>
    }

    <div *transloco="let tApp; prefix: 'appSm'" class="settings-section">
      <div class="layout-menuitem-root-text settings-header">
        <i class="pi pi-cog"></i>
        {{ tApp('categories.settings') }}
      </div>
      <div class="settings-controls">
        <div class="settings-row">
          <span class="settings-label">
            <i class="pi pi-language"></i>
            {{ tApp('settings.language') }}
          </span>
          <idt-i18n />
        </div>
        <div class="settings-row">
          <span class="settings-label">
            <i class="pi pi-circle-fill"></i>
            {{ tApp('settings.theme') }}
          </span>
          <idt-theme-toggle />
        </div>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100%;
      padding-inline-end: 0.75rem;
    }

    .layout-menuitem-root-text i {
      margin-inline-end: 0.5rem;
      font-size: 0.857rem;
    }

    .menu-item-tag {
      margin-inline-start: auto;
    }

    .settings-section {
      padding: 0 0.5rem 1rem;
    }

    .settings-header {
      margin-bottom: 0.75rem;
    }

    .settings-controls {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .settings-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }

    .settings-label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.857rem;
      opacity: 0.8;

      i {
        font-size: 0.857rem;
      }
    }
  `,
  providers: [
    provideTranslocoScopeSharedGlobal(),
    provideTranslocoScopeSharedSideMenuContent(),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenuContentComponent {
  protected readonly i18nStore = inject(I18nStore);
  protected readonly layoutStore = inject(LayoutStore);
  protected readonly platformStore = inject(PlatformStore);
  protected readonly session = inject(APP_SESSION);

  private readonly helpItemsFactory = inject(TOPBAR_HELP_ITEMS_FACTORY);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  readonly sideMenuCategories = input<SideMenuCategory[]>();

  protected readonly mobileHelpExpanded = signal(false);

  private readonly expandedByCategoryId = signal<
    Partial<Record<string, boolean>>
  >({});

  private readonly routerUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );
  protected readonly activeMenuItemIds = computed(() => {
    const currentUrl = this.routerUrl();
    const categories = this.sideMenuCategories() ?? [];
    const activeIds: Record<string, boolean> = {};

    for (const category of categories) {
      const siblingPaths = category.items.map((item) =>
        this.resolveMenuItemPath(item.route)
      );

      for (const item of category.items) {
        if (
          isSideMenuItemRouteActive(
            currentUrl,
            this.resolveMenuItemPath(item.route),
            siblingPaths
          )
        ) {
          activeIds[item.id] = true;
        }
      }
    }

    return activeIds;
  });
  protected readonly expandedCategoryState = computed(() => {
    this.routerUrl();
    const categories = this.sideMenuCategories() ?? [];
    const manual = this.expandedByCategoryId();
    const state: Record<string, boolean> = {};

    for (const category of categories) {
      if (!category.collapsible) {
        state[category.id] = true;
        continue;
      }

      const manualState = manual[category.id];
      if (manualState !== undefined) {
        state[category.id] = manualState;
        continue;
      }

      if (this.categoryHasActiveChild(category)) {
        state[category.id] = true;
        continue;
      }

      state[category.id] = category.defaultExpanded ?? false;
    }

    return state;
  });
  protected readonly mobileHelpConfigs = computed(() => {
    this.i18nStore.language();

    return filterHelpConfigsForSideMenu(
      this.helpItemsFactory(),
      this.sideMenuCategories()
    );
  });
  protected readonly mobileHelpLabeledItems = computed(() => {
    this.i18nStore.language();

    return this.mobileHelpConfigs().map((item) => ({
      ...item,
      label: this.transloco.translate(item.labelKey, {}, 'global'),
    }));
  });
  protected readonly mobileHelpTitle = computed(() => {
    this.i18nStore.language();

    return this.transloco.translate('help.title', {}, 'global');
  });
  protected readonly mobileSearchTitle = computed(() => {
    this.i18nStore.language();

    return this.transloco.translate('search.title', {}, 'global');
  });
  protected readonly showMobileSearchLink = computed(() => {
    const userType = this.session.user()?.type;
    const canSearch = userType === 'buyer' || userType === 'admin';

    return canSearch && !sideMenuHasRoute(this.sideMenuCategories(), '/search');
  });
  protected readonly showMobileHelpSection = computed(
    () =>
      this.mobileHelpLabeledItems().length > 0 || this.showMobileSearchLink()
  );

  protected categoryHasActiveChild(category: SideMenuCategory): boolean {
    const [currentUrl = ''] = this.router.url.split('?');

    return category.items.some((item) =>
      this.routeMatches(currentUrl, item.route)
    );
  }

  protected closeMobileSidebarIfOpen(): void {
    if (this.layoutStore.mobileMenuActive()) {
      this.layoutStore.onMenuToggle();
    }
  }

  protected onCategoryToggleKeydown(event: Event, categoryId: string): void {
    event.preventDefault();
    this.toggleCategory(categoryId);
  }

  protected toggleCategory(categoryId: string): void {
    const categories = this.sideMenuCategories() ?? [];
    const category = categories.find((entry) => entry.id === categoryId);

    if (!category?.collapsible) {
      return;
    }

    this.setCategoryExpanded(
      categoryId,
      !this.expandedCategoryState()[categoryId]
    );
  }

  private normalizeUrlPath(url: string): string {
    return normalizeSideMenuRoutePath(url);
  }

  private resolveMenuItemPath(route: string[]): string {
    return this.normalizeUrlPath(
      this.router.serializeUrl(this.router.createUrlTree(route))
    );
  }

  private routeMatches(currentUrl: string, route: string[]): boolean {
    const normalizedRoute = this.normalizeUrlPath(
      this.router.serializeUrl(this.router.createUrlTree(route))
    );
    const normalizedUrl = this.normalizeUrlPath(currentUrl);

    return (
      normalizedUrl === normalizedRoute ||
      normalizedUrl.startsWith(`${normalizedRoute}/`)
    );
  }

  private setCategoryExpanded(categoryId: string, expanded: boolean): void {
    this.expandedByCategoryId.update((state) => ({
      ...state,
      [categoryId]: expanded,
    }));
  }
}
